import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, Loader2, X } from 'lucide-react';
import { SuggestionCard } from './SuggestionCard';
import { FilmplyBadge } from '../FilmplyBadge';
import type { PersonSuggestion } from '../../types/onboarding';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.filmple.io';

interface SelectedPerson {
  id: number;
  name: string;
  profile_path: string | null;
}

interface PrefetchedActor extends SelectedPerson {
  reason?: string;
}

interface CachedPersonSuggestion extends SelectedPerson {
  reason?: string;
}

interface ActorStepProps {
  selectedGenres: number[];
  selectedMovieIds: number[];
  selectedActors: SelectedPerson[];
  onAddActor: (actor: SelectedPerson) => void;
  onRemoveActor: (actorId: number) => void;
  maxSelections?: number;
  prefetchedSuggestions?: PrefetchedActor[] | null;
  cachedSuggestions?: CachedPersonSuggestion[] | null;
  onSuggestionsChange?: (suggestions: CachedPersonSuggestion[]) => void;
}

// Skeleton placeholder for streaming cards
function SuggestionSkeleton() {
  return (
    <div className="bg-[#1A1F26]/50 rounded-lg p-3 animate-pulse">
      <div className="w-20 h-20 bg-slate-700 rounded-full mx-auto mb-2" />
      <div className="h-4 bg-slate-700 rounded w-3/4 mx-auto mb-1" />
      <div className="h-3 bg-slate-700 rounded w-1/2 mx-auto" />
    </div>
  );
}

export function ActorStep({
  selectedGenres,
  selectedMovieIds,
  selectedActors,
  onAddActor,
  onRemoveActor,
  maxSelections = 3,
  prefetchedSuggestions,
  cachedSuggestions,
  onSuggestionsChange,
}: ActorStepProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SelectedPerson[]>([]);
  const [suggestions, setSuggestions] = useState<PersonSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [streamingComplete, setStreamingComplete] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  const canSelectMore = selectedActors.length < maxSelections;
  const selectedIds = useMemo(() => selectedActors.map((a) => a.id), [selectedActors]);

  // Initialize from cache or prefetch on mount (prevents re-fetch on back navigation)
  useEffect(() => {
    if (initialized) return;

    // First priority: use cached suggestions (from previous visit to this step)
    if (cachedSuggestions && cachedSuggestions.length > 0) {
      setSuggestions(
        cachedSuggestions.map((a) => ({
          id: a.id,
          name: a.name,
          profile_path: a.profile_path,
          known_for_department: 'Acting',
          reason: a.reason || 'Popular actor you might like',
          reasonType: 'from_movie' as const,
        }))
      );
      setStreamingComplete(true);
      setInitialized(true);
      return;
    }

    // Second priority: use prefetched suggestions (from combined endpoint)
    if (prefetchedSuggestions && prefetchedSuggestions.length > 0) {
      const mapped = prefetchedSuggestions.map((a) => ({
        id: a.id,
        name: a.name,
        profile_path: a.profile_path,
        known_for_department: 'Acting',
        reason: a.reason || 'Popular actor you might like',
        reasonType: 'from_movie' as const,
      }));
      setSuggestions(mapped);
      setStreamingComplete(true);
      setInitialized(true);
      // Cache the prefetched suggestions (with reason)
      onSuggestionsChange?.(prefetchedSuggestions.map((a) => ({
        id: a.id,
        name: a.name,
        profile_path: a.profile_path,
        reason: a.reason,
      })));
      return;
    }

    setInitialized(true);
  }, [cachedSuggestions, prefetchedSuggestions, initialized, onSuggestionsChange]);

  // Fetch suggestions with SSE streaming
  const fetchSuggestionsStream = useCallback(() => {
    // Close any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    if (selectedMovieIds.length === 0) {
      setSuggestions([]);
      setStreamingComplete(true);
      return;
    }

    setLoadingSuggestions(true);
    setStreamingComplete(false);

    const newSuggestions: PersonSuggestion[] = [];

    const params = new URLSearchParams({
      genres: selectedGenres.join(','),
      movies: selectedMovieIds.join(','),
      exclude: '',
      selectedActors: '',
    });

    const eventSource = new EventSource(`${API_URL}/api/onboarding/suggestions/actors/stream?${params}`);
    eventSourceRef.current = eventSource;

    eventSource.addEventListener('suggestion', (event) => {
      try {
        const suggestion = JSON.parse(event.data) as PersonSuggestion;
        newSuggestions.push(suggestion);
        setSuggestions([...newSuggestions]);
        setLoadingSuggestions(false);
      } catch (err) {
        console.error('Failed to parse actor suggestion:', err);
      }
    });

    eventSource.addEventListener('complete', () => {
      setLoadingSuggestions(false);
      setStreamingComplete(true);
      // Cache suggestions for back navigation (with reason)
      onSuggestionsChange?.(newSuggestions.map((s) => ({
        id: s.id,
        name: s.name,
        profile_path: s.profile_path,
        reason: s.reason,
      })));
      eventSource.close();
      eventSourceRef.current = null;
    });

    eventSource.addEventListener('error', (event) => {
      console.error('SSE error:', event);
      setLoadingSuggestions(false);
      setStreamingComplete(true);
      if (newSuggestions.length > 0) {
        onSuggestionsChange?.(newSuggestions.map((s) => ({
          id: s.id,
          name: s.name,
          profile_path: s.profile_path,
          reason: s.reason,
        })));
      }
      eventSource.close();
      eventSourceRef.current = null;
    });

    eventSource.onerror = () => {
      setLoadingSuggestions(false);
      setStreamingComplete(true);
      if (newSuggestions.length > 0) {
        onSuggestionsChange?.(newSuggestions.map((s) => ({
          id: s.id,
          name: s.name,
          profile_path: s.profile_path,
          reason: s.reason,
        })));
      }
      eventSource.close();
      eventSourceRef.current = null;
    };
  }, [selectedGenres, selectedMovieIds, onSuggestionsChange]);

  // Trigger fetch only if no cached/prefetched data
  useEffect(() => {
    if (!initialized) return;
    if (suggestions.length > 0 && streamingComplete) return;

    fetchSuggestionsStream();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [initialized, fetchSuggestionsStream]);

  // Search actors
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoadingSearch(true);
      try {
        const params = new URLSearchParams({
          query: searchQuery,
          page: '1',
          department: 'Acting',
        });
        const response = await fetch(`${API_URL}/api/search/person?${params}`);
        if (response.ok) {
          const data = await response.json();
          setSearchResults(
            (data.results || [])
              .slice(0, 8)
              .map((p: any) => ({
                id: p.id,
                name: p.name,
                profile_path: p.profile_path,
              }))
          );
          setShowResults(true);
        }
      } catch (err) {
        console.error('Failed to search actors:', err);
      } finally {
        setLoadingSearch(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery]);

  // Close results when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectActor = (actor: SelectedPerson) => {
    if (selectedIds.includes(actor.id)) {
      onRemoveActor(actor.id);
    } else if (canSelectMore) {
      onAddActor(actor);
      setSearchQuery('');
      setShowResults(false);
    }
  };

  // Calculate how many skeletons to show
  const skeletonsNeeded = Math.max(0, 4 - suggestions.length);

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-white mb-1">Pick favorite actors</h3>
        <p className="text-sm text-slate-400">Up to {maxSelections} actors you love watching</p>
      </div>

      {/* Search */}
      <div ref={searchRef} className="relative">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search for an actor..."
            className="w-full pl-10 pr-10 py-2.5 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
          />
          {loadingSearch && (
            <Loader2 size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
          )}
        </div>

        {/* Search results dropdown */}
        {showResults && searchResults.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg shadow-2xl shadow-black/50 max-h-64 overflow-y-auto">
            {searchResults.map((actor) => {
              const isSelected = selectedIds.includes(actor.id);
              return (
                <button
                  key={actor.id}
                  type="button"
                  onClick={() => handleSelectActor(actor)}
                  disabled={!isSelected && !canSelectMore}
                  className={`w-full flex items-center gap-3 p-2 hover:bg-slate-700 transition-colors ${
                    isSelected ? 'bg-cyan-500/10' : ''
                  } ${!isSelected && !canSelectMore ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="w-10 h-10 bg-slate-700 rounded-full overflow-hidden flex-shrink-0">
                    {actor.profile_path && (
                      <img
                        src={`https://image.tmdb.org/t/p/w92${actor.profile_path}`}
                        alt={actor.name}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm text-white">{actor.name}</p>
                  </div>
                  {isSelected && <span className="text-cyan-400 text-xs">Selected</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Selected actors */}
      {selectedActors.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-slate-400">
            Selected ({selectedActors.length}/{maxSelections}):
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedActors.map((actor) => (
              <span
                key={actor.id}
                className="flex items-center gap-1 px-2 py-1 bg-cyan-500/20 text-cyan-400 text-sm rounded-lg"
              >
                {actor.name}
                <button
                  type="button"
                  onClick={() => onRemoveActor(actor.id)}
                  className="hover:text-white"
                >
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Suggestions */}
      {selectedMovieIds.length > 0 && (
        <div className="space-y-3">
          {loadingSuggestions && suggestions.length === 0 ? (
            <FilmplyBadge thinking />
          ) : (
            <FilmplyBadge text="thinks you'd like:" />
          )}

          {loadingSuggestions && suggestions.length === 0 ? (
            <div className="flex justify-center py-6">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          ) : suggestions.length > 0 || !streamingComplete ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {suggestions.slice(0, 4).map((actor) => (
                <SuggestionCard
                  key={actor.id}
                  type="person"
                  id={actor.id}
                  name={actor.name}
                  profilePath={actor.profile_path}
                  reason={actor.reason}
                  isSelected={selectedIds.includes(actor.id)}
                  onToggle={() =>
                    handleSelectActor({
                      id: actor.id,
                      name: actor.name,
                      profile_path: actor.profile_path,
                    })
                  }
                />
              ))}
              {/* Show skeletons for remaining slots while streaming */}
              {!streamingComplete && skeletonsNeeded > 0 &&
                Array.from({ length: skeletonsNeeded }).map((_, i) => (
                  <SuggestionSkeleton key={`skeleton-${i}`} />
                ))
              }
            </div>
          ) : (
            <p className="text-center text-slate-500 py-4">
              {selectedMovieIds.length === 0
                ? 'Select some movies first to see suggestions'
                : 'No suggestions available'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
